import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import { Modal, Field } from "./Fields";
import ReceiptUploader, { ReceiptBadge, ReceiptGallery } from "../components/ReceiptUploader";
import AddableSelect from "../components/AddableSelect";
import { api, fmtUSD, todayISO, EXPENSE_CATEGORIES } from "../lib/api";
import { downloadCsv } from "../lib/csv";
import { useYear } from "../context/YearContext";
import { Plus, Fuel, Trash2, Pencil, Download } from "lucide-react";
import { toast } from "sonner";

const blank = { date: todayISO(), category: "seed", vendor: "", description: "", amount: "", field_id: "", plot_id: "", receipts: [], fleet_unit_id: "", include_in_cost_share: true };

export default function Expenses({ scopeBusinessId = "farm" }) {
  const { year, refreshYears } = useYear();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [fields, setFields] = useState([]);
  const [customCats, setCustomCats] = useState([]);
  const [fleetUnits, setFleetUnits] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [filter, setFilter] = useState("");
  const [galleryRow, setGalleryRow] = useState(null);

  // Mobile FAB: /expenses?quick=1 auto-opens the add modal
  useEffect(() => {
    if (searchParams.get("quick") === "1") {
      setEditing(null);
      setForm({ ...blank, date: todayISO() });
      setOpen(true);
      searchParams.delete("quick");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const load = async () => {
    const reqs = [
      api.get(`/expenses?year=${year}&business_id=${scopeBusinessId}`),
      api.get(`/fields?year=${year}`),
      api.get(`/options/expense_category`),
    ];
    if (scopeBusinessId === "jk_trucking") {
      reqs.push(api.get(`/fleet-units?business_id=jk_trucking`));
    }
    const results = await Promise.all(reqs);
    const [r, f, c] = results;
    setRows(r.data);
    setFields(f.data);
    setCustomCats(c.data);
    if (scopeBusinessId === "jk_trucking" && results[3]) setFleetUnits(results[3].data);
    else setFleetUnits([]);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [year, scopeBusinessId]);

  const filtered = filter ? rows.filter((r) => r.category === filter) : rows;
  const total = filtered.reduce((s, r) => s + (r.amount || 0), 0);
  const hasMasked = filtered.some((r) => r.amount_masked || r.amount === null);

  // Combined category list: built-in + user customs (deduped, preserving order)
  const allCats = (() => {
    const seen = new Set();
    const out = [];
    for (const c of [...EXPENSE_CATEGORIES, ...customCats.map((c) => ({ value: c.value, label: c.label }))]) {
      if (seen.has(c.value)) continue;
      seen.add(c.value);
      out.push(c);
    }
    return out;
  })();
  const catLabel = (v) => allCats.find((c) => c.value === v)?.label || v;

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: parseFloat(form.amount) || 0, year, business_id: scopeBusinessId };
    try {
      let res;
      if (editing) {
        res = await api.put(`/expenses/${editing.id}`, payload);
        toast.success(res?.data?._offline_draft ? "Saved offline — will sync when back online" : "Expense updated");
      } else {
        res = await api.post("/expenses", payload);
        toast.success(res?.data?._offline_draft ? "Saved offline — will sync when back online" : "Expense added");
      }
      setOpen(false);
      setEditing(null);
      setForm({ ...blank, date: todayISO() });
      refreshYears();
      load();
    } catch {
      toast.error("Could not save expense");
    }
  };

  const onEdit = (r) => {
    setEditing(r);
    setForm({
      date: r.date,
      category: r.category,
      vendor: r.vendor || "",
      description: r.description || "",
      amount: r.amount,
      field_id: r.field_id || "",
      plot_id: r.plot_id || "",
      receipts: r.receipts || [],
      include_in_cost_share: r.include_in_cost_share !== false,
    });
    setOpen(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    await api.delete(`/expenses/${id}`);
    toast.success("Expense deleted");
    load();
  };

  return (
    <div>
      <TopBar
        title="Input Costs & Expenses"
        subtitle={`Every dollar spent running your operation in ${year} — this is what shows up on Schedule F.`}
        actions={
          <div className="flex items-center gap-2 mt-6">
            <button
              data-testid="export-expenses-csv"
              onClick={() => downloadCsv(`expenses-${year}.csv`, filtered.map((r) => ({
                date: r.date, category: r.category, vendor: r.vendor || "",
                description: r.description || "", amount: r.amount, field_id: r.field_id || "",
              })))}
              disabled={!filtered.length}
              className="border border-[#1F1E1B] px-4 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#1F1E1B] hover:text-white transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" /> CSV
            </button>
            <button
              data-testid="add-expense-btn"
              onClick={() => { setEditing(null); setForm({ ...blank, date: todayISO() }); setOpen(true); }}
              className="bg-[#BC4727] text-white px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#9D391E] transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> New Expense
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <div className="bg-white border border-[#1F1E1B] p-4 md:col-span-2 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">
              {filter ? catLabel(filter) : "All Categories"} Total
            </div>
            <div className="font-display text-3xl font-black tabular">{hasMasked ? <span title="Some expenses hidden by role">{fmtUSD(total)}<span className="text-xs text-[#66645E] ml-1">*</span></span> : fmtUSD(total)}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Entries</div>
            <div className="font-display text-3xl font-black tabular">{filtered.length}</div>
          </div>
        </div>
        <div className="bg-white border border-[#1F1E1B] p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] mb-1.5">Filter Category</div>
          <select
            data-testid="expense-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
          >
            <option value="">All categories</option>
            {allCats.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Fuel} title="No expenses recorded" hint="Capture receipts as you go — your tax report depends on it." testid="empty-expenses" />
      ) : (
        <div className="bg-white border border-[#1F1E1B] overflow-x-auto">
          <table className="ledger-table" data-testid="expenses-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Category</th>
                <th>Vendor</th>
                <th>Description</th>
                <th className="num">Amount</th>
                <th>Receipts</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                return (
                  <tr key={r.id} data-testid={`expense-row-${r.id}`}>
                    <td className="tabular">{r.date}</td>
                    <td><span className="stamp">{catLabel(r.category)}</span></td>
                    <td>{r.vendor || "—"}</td>
                    <td className="text-[#66645E]">{r.description || "—"}</td>
                    <td className="num font-bold">{r.amount_masked || r.amount === null ? <span className="text-[#66645E]" data-testid={`expense-amount-masked-${r.id}`}>••••</span> : fmtUSD(r.amount)}</td>
                    <td>
                      <ReceiptBadge receipts={r.receipts} onClick={() => setGalleryRow(r)} testid={`expense-receipts-${r.id}`} />
                    </td>
                    <td>
                      <div className="flex gap-1.5 justify-end">
                        <button data-testid={`edit-expense-${r.id}`} onClick={() => onEdit(r)} className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white transition-colors"><Pencil className="w-3 h-3" /></button>
                        <button data-testid={`delete-expense-${r.id}`} onClick={() => onDelete(r.id)} className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white transition-colors"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <Modal title={editing ? "Edit Expense" : "New Expense"} onClose={() => setOpen(false)}>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="expense-form">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Date" required>
                <input data-testid="expense-date-input" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Category" required>
                <AddableSelect
                  domain="expense_category"
                  defaults={EXPENSE_CATEGORIES}
                  value={form.category}
                  onChange={(v) => setForm({ ...form, category: v })}
                  testid="expense-category-select"
                  required
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Vendor">
                <input data-testid="expense-vendor-input" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="Tractor Supply Co." className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Amount ($)" required>
                <input data-testid="expense-amount-input" type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
            </div>
            <Field label="Description">
              <input data-testid="expense-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <Field label="Associated Field (optional)">
              <select data-testid="expense-field-select" value={form.field_id} onChange={(e) => setForm({ ...form, field_id: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                <option value="">— None —</option>
                {fields.map((f) => <option key={f.id} value={f.id}>{f.name}{f.partner_id ? ` · shared` : ""}</option>)}
              </select>
            </Field>
            {(() => {
              const f = fields.find((x) => x.id === form.field_id);
              if (!f || !f.partner_id) return null;
              return (
                <label className="flex items-start gap-2 bg-[#FAF7EC] border border-[#E5E2D6] p-2.5 cursor-pointer" data-testid="expense-costshare-toggle">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={form.include_in_cost_share !== false}
                    onChange={(e) => setForm({ ...form, include_in_cost_share: e.target.checked })}
                  />
                  <div className="text-xs">
                    <div className="font-bold text-[#5C4A2B]">Include in cost-share billing</div>
                    <div className="text-[#66645E]">
                      This field is shared with a partner at {f.partner_split_pct}%. Uncheck to keep this expense off their next invoice.
                    </div>
                  </div>
                </label>
              );
            })()}
            {scopeBusinessId === "jk_trucking" && fleetUnits.length > 0 && (
              <Field label="Associated Truck/Trailer (optional)">
                <select data-testid="expense-fleet-select" value={form.fleet_unit_id} onChange={(e) => setForm({ ...form, fleet_unit_id: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                  <option value="">— Unallocated —</option>
                  {fleetUnits.map((u) => <option key={u.id} value={u.id}>{u.nickname} ({u.kind})</option>)}
                </select>
              </Field>
            )}
            <ReceiptUploader
              receipts={form.receipts}
              onChange={(receipts) => setForm({ ...form, receipts })}
              testidPrefix="expense-receipt"
              onOcr={(ocr) => {
                // Only fill blanks — never clobber what the user already typed.
                setForm((prev) => ({
                  ...prev,
                  vendor: prev.vendor?.trim() ? prev.vendor : (ocr.vendor || prev.vendor),
                  amount: prev.amount ? prev.amount : (ocr.amount > 0 ? ocr.amount : prev.amount),
                  date: prev.date && prev.date !== todayISO() ? prev.date : (ocr.date || prev.date),
                  category: prev.category && prev.category !== "fuel" ? prev.category : (ocr.category_hint || prev.category),
                }));
              }}
            />
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setOpen(false)} data-testid="expense-cancel-btn" className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="expense-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save Expense</button>
            </div>
          </form>
        </Modal>
      )}

      {galleryRow && (
        <ReceiptGallery receipts={galleryRow.receipts} onClose={() => setGalleryRow(null)} />
      )}
    </div>
  );
}
